import React, { useMemo } from "react";
import { Col, Row, Skeleton } from "antd";

import { useFetchConstant } from "apis/constant";
import { constructDisplayPrice, getLabelOfConstant } from "utils/general";

import {
  BenefitRow,
  CardCol,
  Description,
  DescriptionCol,
  PolicyCardContainer,
  PolicyCardGrid,
  Title,
  TitleCol,
  SubTitle,
} from "./PolicyCard.styles";
import Locale from "react-intl-universal";

const constructPolicyDescriptions = ({
  type,
  premium,
  paymentMode,
  medicalYearlyLimit,
}) => {
  const details = [];
  if (type) {
    details.push({
      label: "Type of Plan",
      value: type,
    });
  }

  if (premium) {
    details.push({
      label: "Premium",
      value: `${constructDisplayPrice(
        Number(premium) || 0.0
      )} (${paymentMode})`,
    });
  }

  if (medicalYearlyLimit) {
    details.push({
      label: "Medical Yearly Limit ",
      value: constructDisplayPrice(Number(medicalYearlyLimit)),
    });
  }

  return details;
};

const constructBenefitList = (benefits) => {
  const { planCoverages, waivers } = benefits.planCoverages.reduce(
    (benefitObj, benefit) => {
      if (benefit.type === "tpdWaiver" || benefit.type === "ciWaiver") {
        benefitObj.waivers[benefit.type] = true;
      } else {
        benefitObj.planCoverages[benefit.type] = benefit.amount;
      }
      return benefitObj;
    },
    { planCoverages: {}, waivers: {} }
  );

  return [
    {
      label: "Death Benefit",
      value: planCoverages.deathBenefit || 0.0,
    },
    {
      label: "TPD Benefit",
      value: planCoverages.tpdBenefit || 0.0,
    },
    {
      label: "Critical Illness (CI)",
      value: planCoverages.ciBenefit || 0.0,
    },
    {
      label: "Total Early CI Benefit",
      value: planCoverages.totalEarlyCIBenefit || 0.0,
    },
    {
      label: "Personal Accident Benefit",
      value: planCoverages.personalAccidentBenefit || 0.0,
    },
    {
      label: "Old Age Disability Benefit",
      value: planCoverages.oldAgeDisabilityBenefit || 0.0,
    },
    {
      label: "Permanent Disability MONTHLY Income",
      value: planCoverages.permanentDisabilityMonthlyIncome || 0.0,
    },
    {
      label: "TPD Premium Waiver",
      value: waivers.tpdWaiver || false,
    },
    {
      label: "CI Premium Waiver",
      value: waivers.ciWaiver || false,
    },
  ];
};

const constructPolicyCardActions = (
  disableEditButton,
  disableRemoveButton,
  onEditClick,
  onDeleteClick
) => {
  const actions = [];
  if (!disableEditButton) {
    actions.push(
      <span onClick={onEditClick}>
        {Locale.get("addNewAdvisee.financialInfo.edit")}
      </span>
    );
  }
  if (!disableRemoveButton) {
    actions.push(
      <span onClick={onDeleteClick}>
        {Locale.get("addNewAdvisee.financialInfo.remove")}
      </span>
    );
  }
  return actions;
};

const PolicyCard = ({
  name,
  company,
  type,
  premium,
  paymentMode,
  medicalCard,
  remarks,
  benefits,
  onEditClick,
  onDeleteClick,
  disableRemoveButton,
  disableEditButton,
}) => {
  const {
    isLoading: isPlanTypesLoading,
    selection: planTypes,
  } = useFetchConstant("planTypes");
  const {
    isLoading: isInsuranceCompaniesLoading,
    selection: insuranceCompanies,
    data: insuranceCompaniesConst,
  } = useFetchConstant("insuranceCompanies");
  const {
    isLoading: isPaymentModeLoading,
    selection: paymentModes,
  } = useFetchConstant("paymentModes");
  const isConstantsLoading =
    isPlanTypesLoading || isInsuranceCompaniesLoading || isPaymentModeLoading;

  const paymentModeLabel = useMemo(
    () =>
      isPaymentModeLoading ? "" : getLabelOfConstant(paymentMode, paymentModes),
    [isPaymentModeLoading, paymentModes, paymentMode]
  );
  const policyTypeLabel = useMemo(
    () => (isPlanTypesLoading ? "" : getLabelOfConstant(type, planTypes)),
    [isPlanTypesLoading, planTypes, type]
  );
  const insuranceCompanyLabel = useMemo(
    () =>
      isInsuranceCompaniesLoading
        ? ""
        : company.type === insuranceCompaniesConst.OTHERS.code
        ? `(${insuranceCompaniesConst.OTHERS.label}) ${company.label}`
        : getLabelOfConstant(company.type, insuranceCompanies),
    [
      isInsuranceCompaniesLoading,
      insuranceCompanies,
      insuranceCompaniesConst,
      company,
    ]
  );

  const actions = constructPolicyCardActions(
    disableEditButton,
    disableRemoveButton,
    onEditClick,
    onDeleteClick
  );
  const allBenefits = constructBenefitList(benefits);
  const descriptions = constructPolicyDescriptions({
    type: policyTypeLabel,
    premium,
    paymentMode: paymentModeLabel,
    medicalYearlyLimit: medicalCard ? medicalCard.yearlyLimitAmt : undefined,
    benefits,
  });

  return (
    <PolicyCardContainer actions={actions}>
      <Skeleton loading={isConstantsLoading} avatar active>
        <Row>
          <CardCol span={24} md={10} lg={14}>
            <PolicyCardGrid hoverable={false}>
              <Row>
                <TitleCol span={24}>
                  <Title>{name}</Title>
                  <Description highlight>{insuranceCompanyLabel}</Description>
                </TitleCol>
                {descriptions.map((description) => (
                  <DescriptionCol
                    span={24}
                    lg={12}
                    xl={8}
                    key={description.label}
                  >
                    <SubTitle>{description.label}</SubTitle>
                    <Description>{description.value}</Description>
                  </DescriptionCol>
                ))}
                {remarks && (
                  <DescriptionCol span={24} key="Remarks">
                    <SubTitle>Remarks</SubTitle>
                    <Description>{remarks}</Description>
                  </DescriptionCol>
                )}
              </Row>
            </PolicyCardGrid>
          </CardCol>
          <CardCol span={24} md={14} lg={10}>
            <PolicyCardGrid spacingBeforeContent="16px" hoverable={false}>
              <BenefitRow align="top" gutter={[16, 8]}>
                {allBenefits
                  .filter((allBenefit) => allBenefit.value)
                  .map((allBenefit, index) => (
                    <Col span={24} xxl={12} key={index}>
                      <Description>
                        <b>{allBenefit.label}</b>
                      </Description>
                      <Description>
                        {allBenefit.value > 0 &&
                          typeof allBenefit.value === "number" &&
                          `${constructDisplayPrice(allBenefit.value)}`}
                      </Description>
                    </Col>
                  ))}
              </BenefitRow>
            </PolicyCardGrid>
          </CardCol>
        </Row>
      </Skeleton>
    </PolicyCardContainer>
  );
};

export default PolicyCard;
